import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';

export type ApVendorRowForActions = {
  linked?: boolean | number;
  VendorId?: number | null;
  ExemptVendorId?: number | null;
  ClientRegisteredVendorId?: number | null;
  isExempt?: number | boolean;
  hasMetaData?: boolean;
  ApConfig?: {
    apSystem?: string;
    showExemptChange?: boolean;
    v3CutOverDate?: Date | string | null;
  };
};

export type ApConfigForClientManaged = {
  apSystem?: string | null;
  v3CutOverDate?: Date | string | null;
};

export type ApConfigForLabel = {
  id?: number;
  apSystem?: string | null;
  apDisplayName?: string | null;
  apInstance?: string | null;
};

/**
 * How an AP configuration is named in the UI. Shared so the picker and any
 * screen that echoes the chosen configuration back cannot disagree.
 */
export function getApConfigDisplayLabel(apConfig: ApConfigForLabel): string {
  const name =
    apConfig.apDisplayName || apConfig.apSystem || `AP Config #${apConfig.id}`;

  return apConfig.apInstance ? `${name} (${apConfig.apInstance})` : name;
}

/**
 * Whether a Client Managed flag can exist on this AP configuration at all.
 *
 * Client Managed is an `ApVendorMeta` row, which only applies to Vantaca
 * configurations past their v3 cut-over. Kept next to the row-level action
 * rules so the Vantaca + v3 truth table is stated once — the bulk upload
 * (VPDW-4365) and the per-row +/- action must not drift apart.
 */
export function apConfigSupportsClientManaged(
  apConfig: ApConfigForClientManaged
): boolean {
  return (
    apConfig.apSystem === 'Vantaca' &&
    !!apConfig.v3CutOverDate &&
    new Date(apConfig.v3CutOverDate) <= new Date()
  );
}

/**
 * Why `apConfigSupportsClientManaged` said no, phrased for a person. Null when
 * the configuration is eligible.
 */
export function getClientManagedIneligibleReason(
  apConfig: ApConfigForClientManaged
): string | null {
  if (apConfig.apSystem !== 'Vantaca') {
    return 'Client Managed not supported';
  }

  if (!apConfig.v3CutOverDate) {
    return 'Not yet cut over to v3';
  }

  if (new Date(apConfig.v3CutOverDate) > new Date()) {
    return 'Not yet cut over';
  }

  return null;
}

export function isApVendorLinked(rowData: ApVendorRowForActions): boolean {
  return !!(
    rowData.linked === true ||
    rowData.linked === 1 ||
    rowData.VendorId ||
    rowData.ExemptVendorId ||
    rowData.ClientRegisteredVendorId
  );
}

export function getApVendorClientManagedColumnActions(
  rowData: ApVendorRowForActions,
  apSystem?: string
): GridActionColumnActions[] {
  const gridActionColumnActionArr: GridActionColumnActions[] = [
    'invite',
    'refresh'
  ];

  const effectiveApSystem = apSystem ?? rowData.ApConfig?.apSystem;

  if (effectiveApSystem !== 'Vantaca' || isApVendorLinked(rowData)) {
    return gridActionColumnActionArr;
  }

  if (!rowData.ApConfig?.v3CutOverDate && rowData.ApConfig?.showExemptChange) {
    gridActionColumnActionArr.unshift(
      rowData.isExempt === 1 ? 'remove' : 'add'
    );
  }

  const isV3 =
    rowData.ApConfig?.v3CutOverDate &&
    new Date(rowData.ApConfig.v3CutOverDate) <= new Date();

  if (isV3) {
    gridActionColumnActionArr.unshift(rowData.hasMetaData ? 'remove' : 'add');
  }

  return gridActionColumnActionArr;
}
